import React, { Suspense, useEffect, useState } from "react";
import "./global.scss";
import "./index.scss";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import store from "./store/store.ts";
import { Provider } from "react-redux";
import Loading from "./components/app/Loading";
import "./utils/lokalize.ts";
import { initAuth0Client, setHubApiUrl } from "jwt-storage-lib";
import * as Sentry from "@sentry/react";
import PrivatePage from "./PrivatePage.tsx";
import StyledToaster from "./StyledToast.tsx";
import Gleap from "gleap";
import useWindowSize from "./hooks/useWindowSize.ts";

function Bootstrap() {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        async function initialize() {
            setHubApiUrl(import.meta.env.VITE_URL_HUB_API);
            // setRealmApiId(import.meta.env.VITE_MONGO_APP_ID);
            await initAuth0Client({
                domain: import.meta.env.VITE_AUTH0_DOMAIN,
                clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
                redirectUri: window.location.origin,
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                scope: "openid profile email offline_access"
            });
            if (!isCancelled) {
                setReady(true);
            }
        }

        void initialize();
        return () => {
            isCancelled = true;
        };
    }, []);

    const { isMobile } = useWindowSize();
    const isOnboardPage = new URLSearchParams(window.location.search).has("onboard");
    useEffect(() => {
        if (!isOnboardPage || !isMobile) {
            Gleap.initialize("KAbTqk9MRmgsjIqDChUAABeuAntfUNme");
        }
    }, [isOnboardPage, isMobile]);
    if (!ready) {
        return <Loading />;
    }

    return (
        <PrivatePage>
            <App />
        </PrivatePage>
    );
}

const redactUrl = (url: string) => {
    try {
        const urlObj = new URL(url);
        if (urlObj.pathname.includes("reset-password")) {
            urlObj.pathname = "/reset-password/:token";
        }
        urlObj.search = "";
        return urlObj.toString();
    } catch {
        return url;
    }
};

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === "true",
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",
    release: import.meta.env.VITE_SENTRY_RELEASE || "builder@dev",
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) : 0.0,
    profilesSampleRate: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE ? parseFloat(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE) : 0.0,
    debug: import.meta.env.VITE_SENTRY_DEBUG === "true",
    integrations: [
        Sentry.browserProfilingIntegration(),
        Sentry.captureConsoleIntegration({
            levels: ["error"]
        }),
        Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true
        })
    ],
    tracePropagationTargets: [
        "localhost",
        "127.0.0.1:8080",
        /^https?:\/\/(?!auth0?\.)[^.]*\.finalpos\.com/,
        /^https:\/\/.*\.finalcommerce\.com/,
        /^https:\/\/.*\.us-central1\.run\.app$/
    ],
    beforeSend(event) {
        // Add enhanced context to all automatically captured errors
        if (event.exception || event.message) {
            event.tags = {
                ...event.tags,
                component: event.tags?.component || "AutoCaptured"
            };
            event.extra = {
                ...event.extra,
                url: redactUrl(window.location.href)
            };
        }
        return event;
    }
});

Gleap.initialize(import.meta.env.VITE_GLEAP_API_KEY);

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <Provider store={store}>
            <Suspense fallback={<Loading />}>
                <Bootstrap />
                <StyledToaster />
            </Suspense>
        </Provider>
    </React.StrictMode>
);
